import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import Pusher from 'pusher-js';
import logo from '../../images/logo.png';
import logoProf from '../../images/logo_prof.png';
import NotificationBell from '../NotificationBell/index.js';
import UserMenuButton from '../UserMenuButton/index.js';
import { ROLE_PROFESSOR } from '../../constants/roles';
import useUserMenu from '../../menus/useUserMenu';
import profMenu from '../../menus/prof_menu';
import './Drawer.css';

function Logo() {
    const isProfessor = useSelector((state) => state.auth.user?.role === ROLE_PROFESSOR);
    return (
        <img src={isProfessor ? logoProf : logo} alt="Trading Series" style={{ height: 40, width: 'auto' }} />
    );
}

function NavIcon({ name }) {
    const c = 'currentColor';
    switch (name) {
        case 'home':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><path d="M3.5 9.5L11 3.5l7.5 6v8.5a1 1 0 01-1 1h-4v-6h-5v6h-4a1 1 0 01-1-1V9.5z" stroke={c} strokeWidth="1.6" strokeLinejoin="round"/></svg>;
        case 'wallet':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><rect x="3" y="6" width="16" height="12" rx="2.5" stroke={c} strokeWidth="1.6"/><path d="M3 9.5h13a3 3 0 010 6H3" stroke={c} strokeWidth="1.6"/><circle cx="16" cy="12.5" r="1.1" fill={c}/></svg>;
        case 'markets':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><path d="M3 14l4-5 3 3 4-7 5 9" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/><path d="M3 18.5h16" stroke={c} strokeWidth="1.6" strokeLinecap="round"/></svg>;
        case 'trophy':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><path d="M7 4h8v5a4 4 0 11-8 0V4z" stroke={c} strokeWidth="1.6" strokeLinejoin="round"/><path d="M5 5H3v2a2 2 0 002 2M17 5h2v2a2 2 0 01-2 2M9 17h4M11 13v4" stroke={c} strokeWidth="1.6" strokeLinecap="round"/></svg>;
        case 'user':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><circle cx="11" cy="8" r="3.5" stroke={c} strokeWidth="1.6"/><path d="M4 19c0-3.6 3.1-6 7-6s7 2.4 7 6" stroke={c} strokeWidth="1.6" strokeLinecap="round"/></svg>;
        case 'teams':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><circle cx="8" cy="8" r="3" stroke={c} strokeWidth="1.6"/><circle cx="15" cy="9" r="2.4" stroke={c} strokeWidth="1.6"/><path d="M3 18c0-3 2.2-5 5-5s5 2 5 5M14 18c0-2.2 1.5-4 4-4" stroke={c} strokeWidth="1.6" strokeLinecap="round"/></svg>;
        case 'video':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><rect x="3" y="6" width="12" height="10" rx="2" stroke={c} strokeWidth="1.6"/><path d="M15 9.5l4-2.5v8l-4-2.5" stroke={c} strokeWidth="1.6" strokeLinejoin="round"/></svg>;
        case 'document':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><path d="M6 3h7l4 4v12a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" stroke={c} strokeWidth="1.6" strokeLinejoin="round"/><path d="M8 11h6M8 14.5h6" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>;
        case 'admin':
            return <svg width={20} height={20} viewBox="0 0 22 22" fill="none"><rect x="3" y="6" width="16" height="12" rx="2" stroke={c} strokeWidth="1.6"/><path d="M3 9.5h16M7 6V4.5a1 1 0 011-1h6a1 1 0 011 1V6" stroke={c} strokeWidth="1.6"/></svg>;
        default:
            return <div style={{ width: 20, height: 20 }}/>;
    }
}

const Drawer = ({ toggleDrawer, isDrawerOpen, isScrollingDown }) => {
    const navigate = useNavigate();
    const role = useSelector((state) => state.auth.user?.role);
    const isProfessor = role === ROLE_PROFESSOR;
    const userMenu = useUserMenu();
    const menuLinks = isProfessor ? profMenu : userMenu;
    const homePath = isProfessor ? '/espace-professeur/vue-ensemble' : '/accueil';
    const [notification, setNotification] = useState('');

    useEffect(() => {
        // Les professeurs n'ont pas la fonctionnalité de vidéos déclenchées
        if (isProfessor) return;

        const pusher = new Pusher('2f812ff57f4c4ac523e3', {
            cluster: 'eu',
            encrypted: true,
        });
        const channel = pusher.subscribe('video-channel');
        channel.bind('video-available', (data) => {
            setNotification(`Nouvelle vidéo disponible : ${data.video.title}`);
        });
        return () => {
            pusher.unsubscribe('video-channel');
        };
    }, [isProfessor]);

    const handleLinkClick = (path) => {
        navigate(path);
        toggleDrawer();
    };

    return (
        <nav className={`lg:hidden top-navbar-mobile${isScrollingDown ? ' scrolling-bottom' : ''}`} style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            zIndex: 50,
        }}>
            <div className="drawer-topbar" style={{
                background: 'transparent',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px 12px',
            }}>
                <div style={{ cursor: 'pointer' }} onClick={() => navigate(homePath)}>
                    <Logo />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {notification && (
                        <span style={{
                            fontSize: 11, color: '#E0C281',
                            maxWidth: 160, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{notification}</span>
                    )}
                    {!isProfessor && <NotificationBell variant="mobile" />}
                    <button onClick={toggleDrawer} aria-label="menu" style={{
                        width: 38, height: 38, borderRadius: 12,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        display: 'grid', placeItems: 'center',
                        cursor: 'pointer', color: '#F2F5FB', padding: 0,
                        touchAction: 'manipulation',
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
            </div>
            </div>

            {/* Backdrop */}
            <div
                onClick={toggleDrawer}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 99,
                    opacity: isDrawerOpen ? 1 : 0,
                    pointerEvents: isDrawerOpen ? 'auto' : 'none',
                    transition: 'opacity 0.28s ease',
                }}
            />

            {/* Panel */}
            <div style={{
                position: 'fixed', top: 0, right: 0,
                width: '22rem', height: '100%',
                background: 'rgba(10,18,38,0.3)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderLeft: '1px solid rgba(255,255,255,0.08)',
                zIndex: 100,
                display: 'flex', flexDirection: 'column',
                padding: '20px 24px',
                transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
                        {/* Logo — identique au header */}
                        <div style={{ cursor: 'pointer' }} onClick={() => navigate(homePath)}>
                            <img src={isProfessor ? logoProf : logo} alt="Trading Series" style={{ height: 32, width: 'auto' }} />
                        </div>

                        {/* Croix fermeture */}
                        <button onClick={toggleDrawer} style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            borderRadius: 10,
                            width: 36, height: 36,
                            display: 'grid', placeItems: 'center',
                            cursor: 'pointer', color: '#F2F5FB',
                            touchAction: 'manipulation',
                        }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        {menuLinks.map(({ label, path, icon }, index) => (
                            <button key={path} onClick={() => handleLinkClick(path)} style={{
                                background: 'none', border: 'none',
                                padding: '14px 8px',
                                textAlign: 'left', cursor: 'pointer',
                                color: '#F2F5FB',
                                fontSize: 16, fontWeight: 600,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                display: 'flex', alignItems: 'center', gap: 14,
                                opacity: isDrawerOpen ? 1 : 0,
                                transform: isDrawerOpen ? 'translateY(0)' : 'translateY(10px)',
                                transition: 'opacity 220ms ease, transform 220ms ease',
                                transitionDelay: isDrawerOpen ? `${80 + index * 55}ms` : '0ms',
                            }}>
                                <NavIcon name={icon} />
                                {label}
                            </button>
                        ))}
                    </div>

                    <div style={{
                        opacity: isDrawerOpen ? 1 : 0,
                        transform: isDrawerOpen ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'opacity 220ms ease, transform 220ms ease',
                        transitionDelay: isDrawerOpen ? `${80 + menuLinks.length * 55}ms` : '0ms',
                    }}>
                        <UserMenuButton />
                    </div>
                </div>
        </nav>
    );
};

export default Drawer;
